import { UserGroupsListComponent } from './UserGroupsList.types';
import { FuseOptions } from 'fuse.js';
import { GroupDto } from '../../../../api-client';
import { useMemo } from 'react';
import { useSearch } from '../../../../common/hooks/useSearch/useSearch';
import { Box, Button, Flexbox, Icon, TextField, Typography } from '@hiiretail/synergy-ui';
import { MyGroupsListWrapper, MyGroupsRow } from './UserGroupsList.styles';
import styled from 'styled-components';
import { Divider } from '../../../Profile/ui/GeneralInfoForm/GeneralInfoForm';

const StyledButton = styled(Button)`
  padding: 0;
`;

export const UserGroupsList: UserGroupsListComponent = ({
  groups,
  isAdding,
  isRemoving,
  addUser,
  setUserGroupToDelete,
  onGroupClick,
}) => {
  const isUpdating = isAdding || isRemoving;

  const searchOptions: FuseOptions<GroupDto> = useMemo(
    () => ({
      keys: ['id', 'description', 'alias'],
      threshold: 0.1,
      caseSensitive: false,
      findAllMatches: true,
    }),
    [],
  );

  const { filteredData: filteredGroups, onSearchChange } = useSearch(groups, searchOptions);

  return (
    <MyGroupsListWrapper item container direction="column" width="100%" height="100%">
      <Flexbox item container alignItems="center" justifyContent="space-between">
        <Flexbox item container alignItems="center" gutter={2}>
          <Flexbox item container alignItems="center">
            <Icon.Locker size="md" />
          </Flexbox>
          <Flexbox item>
            <Typography>Groups</Typography>
          </Flexbox>
        </Flexbox>
        <StyledButton variant="icon" data-testid="add-user-to-group" onClick={() => addUser()}>
          <Icon.Plus size="md" />
        </StyledButton>
      </Flexbox>
      <Box my={4}>
        <Divider />
      </Box>
      <Box mb={4}>
        <TextField
          name="effective-groups-search"
          data-testid="effective-groups-search"
          label=""
          hideLabel
          fullWidth
          onChange={(event) => onSearchChange(event.target.value)}
          disabled={groups.length < 1}
          placeholder="Filter by name or description"
        />
      </Box>
      {!isUpdating && !groups.length && (
        <Flexbox container item alignItems="center" justifyContent="center" flex="1 1 auto">
          <Typography color={() => 'rgb(0,0,0,0.54)'}>
            The user is not assigned to any group
          </Typography>
        </Flexbox>
      )}
      {!isUpdating && !!groups.length && !!filteredGroups.length && (
        <Flexbox item container direction="column" gutter={1}>
          {filteredGroups.map((group) => (
            <MyGroupsRow
              item
              container
              role='button'
              key={group.id}
              data-testid={`open-user-group-${group.id}`}
              onClick={() => onGroupClick(group.id)}
            >
              <Flexbox item container direction="column">
                <Flexbox item container gutter={2} alignItems="center">
                  <Flexbox item>
                    <Typography variant="body1" weight="bold">
                      {group.name}
                    </Typography>
                  </Flexbox>
                </Flexbox>
                <Flexbox item>
                  <Typography variant="caption">{group.description || 'No description'}</Typography>
                </Flexbox>
              </Flexbox>
              <Button
                variant="icon"
                data-testid={`remove-user-from-group-${group.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setUserGroupToDelete(group)
                }}
              >
                <Icon.Delete size="md" />
              </Button>
            </MyGroupsRow>
          ))}
        </Flexbox>
      )}
      {!isUpdating && !!groups.length && !filteredGroups.length && (
        <Flexbox container item alignItems="center" flex="1 1 auto">
          <Typography color={() => 'rgb(0,0,0,0.54)'}>
            No groups found, try changing the filter
          </Typography>
        </Flexbox>
      )}
    </MyGroupsListWrapper>
  );
};
